import { useState, useEffect } from 'react';
import {
  getUserSettings,
  updateUserSettings,
  generateAIPrompt,
  uploadProfilePhoto,
  uploadBusinessLogo,
  getMyMember,
  updateMyMember,
} from '../lib/data';
import { getSession } from '../lib/auth';
import PhotoFocusPicker from '../components/PhotoFocusPicker';

export default function Settings({ userId }) {
  const USE_MOCK_DATA = String(import.meta?.env?.VITE_ENABLE_MOCK_DATA ?? 'false').toLowerCase() === 'true';
  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);

  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingProfilePhoto, setIsUploadingProfilePhoto] = useState(false);
  const [isUploadingBusinessLogo, setIsUploadingBusinessLogo] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);

  useEffect(() => {
    loadUserSettings();
  }, []);

  const getDefaultFormData = () => ({
    name: '',
    profileImage: null,
    photoFocusX: null,
    photoFocusY: null,
    businessLogo: null,
    email: '',
    phone: '',
    role: '',
    status: '',
    businessName: '',
    businessAddress: '',
    website: '',
    linkedin: '',
    twitter: '',
    facebook: '',
    googleBusinessProfileUrl: '',
    shortDescription: '',
    longDescription: '',
    sixMonthGoals: '',
    idealReferral: '',
    privacySettings: {
      showToMembers: true,
      showToProspects: false,
      receiveEmails: true,
      receiveTexts: false,
    },
    notificationPreference: 'email',
    industry: '',
    services: '',
  });

  const mapSessionToFormData = (sessionUser) => {
    if (!sessionUser) return {};
    const roleValue = USE_MOCK_DATA
      ? sessionUser.role
      : (sessionUser.backendRole ?? sessionUser.role);
    return {
      name: sessionUser.name ?? '',
      profileImage: sessionUser.photo ?? sessionUser.profileImage ?? null,
      businessLogo: sessionUser.businessLogo ?? null,
      email: sessionUser.email ?? '',
      phone: sessionUser.phone ?? '',
      role: roleValue ?? '',
      status: sessionUser.status ?? '',
      businessName: sessionUser.businessName ?? '',
      website: sessionUser.website ?? '',
      shortDescription: sessionUser.shortDescription ?? '',
      longDescription: sessionUser.longDescription ?? '',
    };
  };

  const mapSettingsToFormData = (settings) => {
    if (!settings) return {};

    // Backend contract: notificationPreference is an object like { email: boolean, sms: boolean }.
    // UI uses a simple enum: email | text | both.
    const pref = settings.notificationPreference;
    const email = Boolean(pref?.email);
    const sms = Boolean(pref?.sms);
    const notificationPreference = sms && email ? 'both' : sms ? 'text' : 'email';

    return {
      businessAddress: settings.businessAddress ?? '',
      linkedin: settings.linkedin ?? '',
      twitter: settings.twitter ?? '',
      facebook: settings.facebook ?? '',
      googleBusinessProfileUrl: settings.googleBusinessProfileUrl ?? '',
      industry: settings.industry ?? '',
      services: settings.services ?? '',
      sixMonthGoals: settings.sixMonthGoals ?? '',
      idealReferral: settings.idealReferral ?? '',
      privacySettings: {
        showToMembers: settings.privacySettings?.showToMembers ?? true,
        showToProspects: settings.privacySettings?.showToProspects ?? false,
        receiveEmails: settings.privacySettings?.receiveEmails ?? true,
        receiveTexts: settings.privacySettings?.receiveTexts ?? false,
      },
      notificationPreference,
    };
  };

  const loadUserSettings = async () => {
    try {
      if (!userId) {
        setFormData(getDefaultFormData());
        setIsLoading(false);
        return;
      }
      console.log('Loading settings for userId:', userId);
      const [settingsResult, sessionResult, memberResult] = await Promise.allSettled([
        getUserSettings(userId),
        getSession(),
        getMyMember(),
      ]);
      const settings = settingsResult.status === 'fulfilled' ? settingsResult.value : null;
      const session = sessionResult.status === 'fulfilled' ? sessionResult.value : null;
      const member = memberResult.status === 'fulfilled' ? memberResult.value : null;

      if (settingsResult.status === 'rejected') {
        console.error('Failed to load settings:', settingsResult.reason);
        setMessage({ type: 'error', text: 'Failed to load settings' });
      }
      if (sessionResult.status === 'rejected') {
        console.error('Failed to load session:', sessionResult.reason);
      }
      if (memberResult.status === 'rejected') {
        console.error('Failed to load member record:', memberResult.reason);
      }

      const baseFormData = getDefaultFormData();
      const merged = {
        ...baseFormData,
        ...mapSessionToFormData(session?.user),
        ...(member ? {
          name: member.name ?? baseFormData.name,
          profileImage: member.photo ?? baseFormData.profileImage,
          photoFocusX: member.photoFocusX ?? baseFormData.photoFocusX,
          photoFocusY: member.photoFocusY ?? baseFormData.photoFocusY,
          businessLogo: member.businessLogo ?? baseFormData.businessLogo,
          businessName: member.businessName ?? baseFormData.businessName,
          website: member.website ?? baseFormData.website,
          shortDescription: member.shortDescription ?? baseFormData.shortDescription,
          longDescription: member.longDescription ?? baseFormData.longDescription,
          phone: member.phone ?? baseFormData.phone,
          email: member.email ?? baseFormData.email,
          role: member.role ?? baseFormData.role,
          status: member.status ?? baseFormData.status,
        } : {}),
        ...mapSettingsToFormData(settings),
      };
      console.log('Received settings:', settings);
      setFormData(merged);
      setUserRole(session?.user?.role || null);
    } catch (error) {
      console.error('Failed to load settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
      setFormData(getDefaultFormData());
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        privacySettings: {
          ...prev.privacySettings,
          [name]: checked
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleGeneratePrompt = async () => {
    setIsGeneratingPrompt(true);
    try {
      const payload = {
        businessName: formData.businessName?.trim(),
        industry: formData.industry?.trim(),
        services: formData.services?.trim(),
      };

      if (!payload.businessName || !payload.industry || !payload.services) {
        setMessage({ type: 'error', text: 'Business name, industry, and services are required' });
        return;
      }

      const response = await generateAIPrompt(payload);
      setFormData(prev => ({
        ...prev,
        shortDescription: response?.shortDescription ?? prev.shortDescription,
        longDescription: response?.longDescription ?? response?.prompt ?? prev.longDescription,
      }));
    } catch (error) {
      console.error('Failed to generate AI prompt:', error);
      setMessage({ type: 'error', text: 'Failed to generate AI prompt' });
    } finally {
      setIsGeneratingPrompt(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const memberPayload = {
        name: formData.name,
        businessName: formData.businessName,
        email: formData.email,
        phone: formData.phone,
        website: formData.website,
        shortDescription: formData.shortDescription,
        longDescription: formData.longDescription,
        photoFocusX: formData.photoFocusX,
        photoFocusY: formData.photoFocusY,
      };

      const [updatedMember, updatedSettings] = await Promise.all([
        updateMyMember(memberPayload),
        updateUserSettings(userId, formData),
      ]);

      setFormData(prev => ({
        ...prev,
        ...(updatedMember ? {
          name: updatedMember.name ?? prev.name,
          profileImage: updatedMember.photo ?? prev.profileImage,
          photoFocusX: updatedMember.photoFocusX ?? prev.photoFocusX,
          photoFocusY: updatedMember.photoFocusY ?? prev.photoFocusY,
          businessLogo: updatedMember.businessLogo ?? prev.businessLogo,
          businessName: updatedMember.businessName ?? prev.businessName,
          website: updatedMember.website ?? prev.website,
          shortDescription: updatedMember.shortDescription ?? prev.shortDescription,
          longDescription: updatedMember.longDescription ?? prev.longDescription,
          phone: updatedMember.phone ?? prev.phone,
          email: updatedMember.email ?? prev.email,
        } : {}),
        ...mapSettingsToFormData(updatedSettings),
      }));

      setMessage({ type: 'success', text: 'Settings saved successfully' });
    } catch (error) {
      console.error('Failed to save settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewInvoices = () => {
    setMessage({ type: 'error', text: 'Invoices are not available yet.' });
  };

  const handleManagePaymentMethods = () => {
    setMessage({ type: 'error', text: 'Payment methods are not available yet.' });
  };

  const handleProfilePhotoUpload = async (file) => {
    if (!file) return;
    setIsUploadingProfilePhoto(true);
    try {
      const response = await uploadProfilePhoto(file, userId);
      setFormData(prev => ({ ...prev, profileImage: response.url }));
      setMessage({ type: 'success', text: 'Profile photo updated' });
    } catch (error) {
      console.error('Failed to upload profile photo:', error);
      setMessage({ type: 'error', text: 'Failed to upload profile photo' });
    } finally {
      setIsUploadingProfilePhoto(false);
    }
  };

  const handleBusinessLogoUpload = async (file) => {
    if (!file) return;
    setIsUploadingBusinessLogo(true);
    try {
      const response = await uploadBusinessLogo(file, userId);
      setFormData(prev => ({ ...prev, businessLogo: response.url }));
      setMessage({ type: 'success', text: 'Business logo updated' });
    } catch (error) {
      console.error('Failed to upload business logo:', error);
      setMessage({ type: 'error', text: 'Failed to upload business logo' });
    } finally {
      setIsUploadingBusinessLogo(false);
    }
  };

  if (isLoading || !userId) {
    return (
      <div className="min-h-[600px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-NBNA-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-semibold text-[#0E4069] mb-6">Settings</h1>

      {message.text && (
        <div className={`mb-6 p-4 rounded-md ${
          message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">

        {/* Personal Information */}
        <section className="bg-white shadow-md rounded-lg p-6">
          <h3 className="mb-4">Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-lato text-NBNA-primary mb-1">
                Full Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="input-field w-full"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-lato text-NBNA-primary mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="input-field w-full"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-lato text-NBNA-primary mb-1">
                Phone
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="input-field w-full"
              />
            </div>
          </div>
        </section>

        {/* Images */}
        <section className="bg-white shadow-md rounded-lg p-6">
          <h3 className="mb-4">Images</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="profileImage" className="block text-sm font-lato text-NBNA-primary mb-1">
                Profile Picture
              </label>
              <div className="mt-1 flex items-center">
                <div className="h-32 w-32 overflow-hidden rounded-full bg-gray-100">
                  <img
                    id="profileImagePreview"
                    src={formData.profileImage || '#'}
                    alt="Profile preview"
                    className={`h-full w-full object-cover ${!formData.profileImage ? 'hidden' : ''}`}
                  />
                </div>
                <label
                  htmlFor="profileImage"
                  className="ml-5 rounded-md border border-gray-300 bg-white py-2 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer"
                >
                  Change
                  <input
                    type="file"
                    id="profileImage"
                    name="profileImage"
                    accept="image/*"
                    className="sr-only"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      handleProfilePhotoUpload(file);
                    }}
                    data-testid="settings-profile-photo-upload"
                  />
                </label>
                {isUploadingProfilePhoto && (
                  <span className="ml-4 text-sm text-NBNA-primary/70">Uploading...</span>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="businessLogo" className="block text-sm font-lato text-NBNA-primary mb-1">
                Business Logo
              </label>
              <div className="mt-1 flex items-center">
                <div className="h-32 w-32 overflow-hidden rounded-lg bg-gray-100">
                  <img
                    id="businessLogoPreview"
                    src={formData.businessLogo || '#'}
                    alt="Logo preview"
                    className={`h-full w-full object-contain ${!formData.businessLogo ? 'hidden' : ''}`}
                  />
                </div>
                <label
                  htmlFor="businessLogo"
                  className="ml-5 rounded-md border border-gray-300 bg-white py-2 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer"
                >
                  Change
                  <input
                    type="file"
                    id="businessLogo"
                    name="businessLogo"
                    accept="image/*"
                    className="sr-only"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      handleBusinessLogoUpload(file);
                    }}
                    data-testid="settings-business-logo-upload"
                  />
                </label>
                {isUploadingBusinessLogo && (
                  <span className="ml-4 text-sm text-NBNA-primary/70">Uploading...</span>
                )}
              </div>
            </div>
          </div>

          {formData.profileImage ? (
            <div className="mt-6 border-t pt-6">
              <label className="block text-sm font-lato text-NBNA-primary mb-1">
                Photo focal point
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Click or drag on your photo to keep the right part in frame across the directory.
              </p>
              <PhotoFocusPicker
                src={formData.profileImage}
                x={formData.photoFocusX}
                y={formData.photoFocusY}
                onChange={(nx, ny) => setFormData((prev) => ({ ...prev, photoFocusX: nx, photoFocusY: ny }))}
              />
            </div>
          ) : null}
        </section>

        {/* Business Details */}
        <section className="bg-white shadow-md rounded-lg p-6">
          <h3 className="mb-4">Business Details</h3>
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label htmlFor="businessName" className="block text-sm font-lato text-NBNA-primary mb-1">
                Business Name
              </label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleInputChange}
                className="input-field w-full"
              />
            </div>
            <div>
              <label htmlFor="businessAddress" className="block text-sm font-lato text-NBNA-primary mb-1">
                Business Address
              </label>
              <input
                type="text"
                id="businessAddress"
                name="businessAddress"
                value={formData.businessAddress}
                onChange={handleInputChange}
                className="input-field w-full"
              />
            </div>
            <div>
              <label htmlFor="website" className="block text-sm font-lato text-NBNA-primary mb-1">
                Website
              </label>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                className="input-field w-full"
              />
            </div>
            <div>
              <label htmlFor="industry" className="block text-sm font-lato text-NBNA-primary mb-1">
                Industry
              </label>
              <input
                type="text"
                id="industry"
                name="industry"
                value={formData.industry}
                onChange={handleInputChange}
                className="input-field w-full"
                data-testid="settings-industry"
              />
            </div>
            <div>
              <label htmlFor="services" className="block text-sm font-lato text-NBNA-primary mb-1">
                Services
              </label>
              <textarea
                id="services"
                name="services"
                value={formData.services}
                onChange={handleInputChange}
                rows={3}
                className="input-field w-full"
                data-testid="settings-services"
              />
            </div>
          </div>
        </section>

        {/* Social Links */}
        <section className="bg-white shadow-md rounded-lg p-6">
          <h3 className="mb-4">Social Media</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="linkedin" className="block text-sm font-lato text-NBNA-primary mb-1">
                LinkedIn
              </label>
              <input
                type="url"
                id="linkedin"
                name="linkedin"
                value={formData.linkedin}
                onChange={handleInputChange}
                className="input-field w-full"
              />
            </div>
            <div>
              <label htmlFor="twitter" className="block text-sm font-lato text-NBNA-primary mb-1">
                Twitter
              </label>
              <input
                type="url"
                id="twitter"
                name="twitter"
                value={formData.twitter}
                onChange={handleInputChange}
                className="input-field w-full"
              />
            </div>
            <div>
              <label htmlFor="facebook" className="block text-sm font-lato text-NBNA-primary mb-1">
                Facebook
              </label>
              <input
                type="url"
                id="facebook"
                name="facebook"
                value={formData.facebook}
                onChange={handleInputChange}
                className="input-field w-full"
              />
            </div>
            <div>
              <label htmlFor="googleBusinessProfileUrl" className="block text-sm font-lato text-NBNA-primary mb-1">
                Google Business Profile
              </label>
              <input
                type="url"
                id="googleBusinessProfileUrl"
                name="googleBusinessProfileUrl"
                value={formData.googleBusinessProfileUrl}
                onChange={handleInputChange}
                className="input-field w-full"
              />
            </div>
          </div>
        </section>

        {/* Descriptions */}
        <section className="bg-white shadow-md rounded-lg p-6">
          <h3 className="mb-4">Business Description</h3>
          <div className="space-y-6">
            <div>
              <label htmlFor="shortDescription" className="block text-sm font-lato text-NBNA-primary mb-1">
                Short Description (max 75 words)
              </label>
              <textarea
                id="shortDescription"
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleInputChange}
                rows={3}
                className="input-field w-full"
                maxLength={300} // Approximate 75 words
              />
            </div>
            <div>
              <label htmlFor="longDescription" className="block text-sm font-lato text-NBNA-primary mb-1">
                Long Description (max 500 words)
              </label>
              <textarea
                id="longDescription"
                name="longDescription"
                value={formData.longDescription}
                onChange={handleInputChange}
                rows={6}
                className="input-field w-full"
                maxLength={2000} // Approximate 500 words
              />
              <button
                type="button"
                disabled={isGeneratingPrompt}
                className="mt-2 text-sm text-NBNA-primary hover:text-NBNA-primary/70 transition-colors disabled:opacity-50"
                onClick={handleGeneratePrompt}
              >
                {isGeneratingPrompt ? 'Generating...' : 'Generate AI Starter Prompt'}
              </button>
            </div>
          </div>
        </section>

        {/* Business Goals */}
        <section className="bg-white shadow-md rounded-lg p-6">
          <h3 className="mb-4">Business Goals</h3>
          <div className="space-y-6">
            <div>
              <label htmlFor="sixMonthGoals" className="block text-sm font-lato text-NBNA-primary mb-1">
                Goals for Next 6 Months
              </label>
              <textarea
                id="sixMonthGoals"
                name="sixMonthGoals"
                value={formData.sixMonthGoals}
                onChange={handleInputChange}
                rows={4}
                className="input-field w-full"
              />
            </div>
            <div>
              <label htmlFor="idealReferral" className="block text-sm font-lato text-NBNA-primary mb-1">
                Ideal Referral Type
              </label>
              <textarea
                id="idealReferral"
                name="idealReferral"
                value={formData.idealReferral}
                onChange={handleInputChange}
                rows={4}
                className="input-field w-full"
              />
            </div>
          </div>
        </section>

        {/* Privacy Settings - Not visible to prospects */}
        {userRole !== 'prospect' && (
          <section className="bg-white shadow-md rounded-lg p-6">
            <h3 className="mb-4">Privacy Settings</h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="showToMembers"
                  name="showToMembers"
                  checked={formData.privacySettings.showToMembers}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-NBNA-primary rounded border-gray-300 focus:ring-NBNA-primary"
                />
                <label htmlFor="showToMembers" className="ml-2 block text-sm font-lato text-NBNA-primary">
                  Display information to members
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="showToProspects"
                  name="showToProspects"
                  checked={formData.privacySettings.showToProspects}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-NBNA-primary rounded border-gray-300 focus:ring-NBNA-primary"
                />
                <label htmlFor="showToProspects" className="ml-2 block text-sm font-lato text-NBNA-primary">
                  Display information to prospects
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="receiveEmails"
                  name="receiveEmails"
                  checked={formData.privacySettings.receiveEmails}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-NBNA-primary rounded border-gray-300 focus:ring-NBNA-primary"
                />
                <label htmlFor="receiveEmails" className="ml-2 block text-sm font-lato text-NBNA-primary">
                  Receive emails
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="receiveTexts"
                  name="receiveTexts"
                  checked={formData.privacySettings.receiveTexts}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-NBNA-primary rounded border-gray-300 focus:ring-NBNA-primary"
                />
                <label htmlFor="receiveTexts" className="ml-2 block text-sm font-lato text-NBNA-primary">
                  Receive texts
                </label>
              </div>
            </div>
          </section>
        )}

        {/* Notification Preferences */}
        <section className="bg-white shadow-md rounded-lg p-6">
          <h3 className="mb-4">Notification Preferences</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-lato text-NBNA-primary mb-2">
                How would you like to receive notifications?
              </label>
              <select
                name="notificationPreference"
                value={formData.notificationPreference}
                onChange={handleInputChange}
                className="select-field w-full"
              >
                <option value="email">Email only</option>
                <option value="text">Text only</option>
                <option value="both">Both email and text</option>
              </select>
            </div>
          </div>
        </section>

        {/* IMPORTANT CONTRACT: Settings must NOT show Executive Branches or Guest Meeting Requests.
            Those belong in /executive only (and guest review can live there too). */}

        {/* Payment & Invoices — hidden for now */}
        {/* <section className="bg-white shadow-md rounded-lg p-6">
          <h3 className="mb-4">Payment & Invoices</h3>
          <div className="space-y-4">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleViewInvoices}
            >
              View Invoices & Receipts
            </button>
            <button
              type="button"
              className="btn-primary ml-4"
              onClick={handleManagePaymentMethods}
            >
              Manage Payment Methods
            </button>
          </div>
        </section> */}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSaving}
            className="btn-primary flex items-center justify-center min-w-[120px]"
          >
            {isSaving ? (
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
